import { useBackend } from '@/composables/useBackend';
import { useColors } from '@/composables/useColors';
import { useDate } from '@/composables/useDate.ts';
import { useLogError } from '@/composables/useLogError';
import { usePlans } from '@/composables/usePlans';
import { useAccountStore } from '@/stores/useAccountStore';
import { useCubeStore } from '@/stores/useCubeStore';
import { useMapStore } from '@/stores/useMapStore';
import { useSettingsStore } from '@/stores/useSettingsStore';
import type { HistoryData } from '@/types/HistoryData';
import type { HistoryUsageData } from '@/types/HistoryUsageData';
import type { TimeSeriesItem } from '@/types/TimeSeriesItem';
import type { TradeData, TradeStatistics } from '@/types/TradeData';
import type { TradingStatisticsRequestParams } from '@/types/TradingStatisticsRequestParams';
import { DateTimeUtil } from '@/util/DateTimeUtil';
import strings from '@/util/Strings';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';

export const useHistoryStore = defineStore('history', () => {
  const { logError } = useLogError()
  const { moment } = useDate()
  const { isFlexPlan: checkFlexPlan, isFloatPlan: checkFloatPlan, isFixPlan: checkFixPlan, isBusinessPlan: checkBusinessPlan } = usePlans()
  const { colorSecondary, colorSecondaryHighContrast } = useColors()

  // --- State ---

  const historySelectedDate = ref<Date>(moment().startOf('day').toDate())

  const historyData = reactive({
    updatedAt: null as any,
    received: false,
    labels: [] as any[],
    datasets: {} as Record<string, any[]>,
    rawDataSets: { consumptionCLP: [] as any[] },
    totals: { avgPrice: false as number | false, avgPriceProduction: false as number | false },
  })

  const tradingStatistics = reactive({
    received: false,
    lastUpdate: false as Date | false,
    consumedFromCommunity: 0,
    consumedFromRetailer: 0,
    producedForCommunity: 0,
    producedForRetailer: 0,
    datasets: {
      consumedEnergyTypes: { data: [] as any[], labels: [] as any[], backgroundColor: [] as any[] },
      producedEnergy: { data: [] as any[], labels: [] as any[], backgroundColor: [] as any[] },
      producers: { data: [] as any[], labels: [] as any[], backgroundColor: [] as any[], raw: [] as any[], cubes: [] as any[] },
      consumers: { data: [] as any[], labels: [] as any[], backgroundColor: [] as any[], raw: [] as any[], cubes: [] as any[] },
      highRes: { producers: [] as any[], consumers: [] as any[] },
    },
  })

  // --- Computed ---

  const isDataReceived = computed(() => historyData.received === true)

  const tradingStatisticsReceived = computed(() => {
    const producedEnergy = tradingStatistics.datasets?.producedEnergy?.data?.filter(e => !!e)?.length > 0
    const consumedEnergy = tradingStatistics.datasets?.consumedEnergyTypes?.data?.length > 0
    return tradingStatistics.received && (consumedEnergy || producedEnergy)
  })

  const chartData = computed(() => ({
    updatedAt: moment(),
    labels: historyData.labels,
    datasets: historyData.datasets,
  }))

  const tradingStatisticsLastUpdate = computed(() => tradingStatistics.lastUpdate)

  const isFlexPlan = computed(() => checkFlexPlan())
  const isFloatPlan = computed(() => checkFloatPlan())
  const isFixPlan = computed(() => checkFixPlan())
  const isBusinessPlan = computed(() => checkBusinessPlan())
  const isMeterless = computed(() => useCubeStore().meterType === 'Meterless')

  const showPastFlex = computed(() => {
    const cube = useCubeStore()

    const planFrom = DateTimeUtil.getOnlyDate(cube.planFrom);
    const planTo = DateTimeUtil.getOnlyDate(cube.planTo);

    return (
      (!planTo || historySelectedDate.value <= (planTo as unknown as Date)) &&
      (!planFrom || historySelectedDate.value >= (planFrom as unknown as Date))
    )
  })

  const showPastFloat = computed(() => {
    const today = new Date()
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    return historySelectedDate.value >= startOfMonth
  })

  const showPastFix = computed(() => {
    const today = new Date()
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    return isFixPlan.value && historySelectedDate.value >= startOfMonth
  })

  // --- Getters ---

  function getTradingStatistics(dataType: string) {
    return tradingStatistics.datasets[dataType]
  }

  function tradingStatisticsAggregate(aggregate: string) {
    return tradingStatistics[aggregate]
  }

  // --- Private data processing ---

  function processTradingStatistics(data: TradeStatistics) {
    const secondaryColor = useAccountStore().isHighContrast
      ? colorSecondaryHighContrast.value
      : colorSecondary.value

    const colors = {
      pv: '#fed600',
      water: '#1966b7',
      wind: '#ff7d2f',
      biogas: '#69c138',
      eeg: '#888',
      battery: '#fed600',
      supplier: secondaryColor,
      unknown: '#dddddd',
    }

    const energyTypes = [
      { value: data.energyTypeData.pv, label: strings.energyTypes.pv, color: colors.pv },
      { value: data.energyTypeData.water, label: strings.energyTypes.water, color: colors.water },
      { value: data.energyTypeData.wind, label: strings.energyTypes.wind, color: colors.wind },
      { value: data.energyTypeData.biogas, label: strings.energyTypes.biogas, color: colors.biogas },
      { value: data.energyTypeData.eeg ?? 0, label: strings.energyTypes.eeg, color: colors.eeg },
      { value: data.energyTypeData.battery ?? 0, label: strings.energyTypes.battery, color: colors.battery },
      { value: data.aggregates.consumed_from_retailer, label: useCubeStore().supplier.name, color: colors.supplier },
    ]

    const consumedEnergyTypes: any = { data: [], labels: [], backgroundColor: [] }
    energyTypes.forEach((row) => {
      if (row.value > 0) {
        consumedEnergyTypes.data.push(row.value)
        consumedEnergyTypes.labels.push(row.label)
        consumedEnergyTypes.backgroundColor.push(row.color)
      }
    })

    const producedEnergy: any = { data: [], labels: [], backgroundColor: [] }
    producedEnergy.data.push(data.aggregates.sold_to_community)
    producedEnergy.labels.push('Community')
    producedEnergy.backgroundColor.push(colors.pv)
    producedEnergy.data.push(data.aggregates.sold_to_retailer)
    producedEnergy.labels.push(useCubeStore().supplier.name)
    producedEnergy.backgroundColor.push(colors.supplier)

    const producers = { data: [], labels: [], backgroundColor: [], raw: [], cubes: [] }
    Object.entries(data.incomingTrades).forEach((row: [string, TradeData]) => {
      if (row[1].consumed > 0) {
        producers.data.push(row[1].consumed)
        producers.labels.push(row[1].nickName)
        producers.backgroundColor.push(colors[row[1].energyType])
        producers.raw.push(row[1])
        producers.cubes.push(row[0])
      }
    })

    const consumers: any = { data: [], labels: [], backgroundColor: [], raw: [], cubes: [] }
    Object.entries(data.outgoingTrades).forEach((row: [string, TradeData]) => {
      if (row[1].sold > 0) {
        consumers.data.push(row[1].sold)
        consumers.labels.push(row[1].nickName)
        consumers.backgroundColor.push(colors[useCubeStore().productionType])
        consumers.raw.push(row[1])
        consumers.cubes.push(row[0])
      }
    })

    let quarterlyTrades: any[] = []
    if (data.highRes) {
      quarterlyTrades = Object.keys(data.highRes.incomingTrades).map((t) => data.highRes.incomingTrades[t])
    }

    let quarterlyTradesProduction: any[] = []
    if (data.highRes) {
      quarterlyTradesProduction = Object.keys(data.highRes.outgoingTrades).map((t) => data.highRes.outgoingTrades[t])
    }

    const lastUpdate = !data.aggregates.period_to ? new Date() : new Date(data.aggregates.period_to)
    if (isNaN(lastUpdate.getTime())) {
      throw new Error(`tradingStatistics received incorrect date for aggregates.period_to: "${data.aggregates.period_to}"`)
    }

    Object.assign(tradingStatistics, {
      received: true,
      lastUpdate,
      consumedFromCommunity: data.aggregates.consumed_from_community * 1000,
      consumedFromRetailer: data.aggregates.consumed_from_retailer * 1000,
      producedForCommunity: data.aggregates.sold_to_community * 1000,
      producedForRetailer: data.aggregates.sold_to_retailer * 1000,
    })
    Object.assign(tradingStatistics.datasets, {
      consumers,
      producers,
      consumedEnergyTypes,
      producedEnergy,
    })
    Object.assign(tradingStatistics.datasets.highRes, {
      producers: quarterlyTrades,
      consumers: quarterlyTradesProduction,
    })
  }

  function processHistoryUsageData(data: HistoryUsageData) {
    const labels: Date[] = []
    const fromCommunity: number[] = []
    const fromSupplier: number[] = []
    const toCommunity: number[] = []
    const toSupplier: number[] = []
    const fromGrid: number[] = []
    const toGrid: number[] = []
    const inverterProduction: number[] = []

    const now: Date = moment().add(1, 'day').startOf('day').toDate()
    const nowInThirtyDays: Date = moment().add(31, 'day').startOf('day').toDate()

    for (const day of data.data) {
      labels.push(moment(day.periodFrom).toDate())
      if (moment(day.periodFrom).toDate() < new Date()) {
        fromCommunity.push(day.usage.fromCommunity)
        fromSupplier.push(day.usage.fromSupplier)
        toCommunity.push(-1 * day.usage.toCommunity)
        toSupplier.push(-1 * day.usage.toSupplier)
        toGrid.push(-1 * day.usage.toGrid)
        fromGrid.push(day.usage.fromGrid)
        inverterProduction.push(day.usage.inverterProduction)
      }
    }

    const forecastConsumption: number[] = []
    const forecastProduction: number[] = []
    const forecastBalance: number[] = []

    let currentLabel: Date = labels?.[0] ?? new Date('')
    let currentSum = 0
    let currentProductionSum = 0

    for (let i = 0; i < data.consumptionTimeSeries.length; i++) {
      const item: TimeSeriesItem = data.consumptionTimeSeries[i]
      const label: Date = moment(item.endt).add(-15, 'minutes').startOf('day').toDate()
      if (label.getTime() !== currentLabel.getTime() || i === data.consumptionTimeSeries.length - 1) {
        currentLabel = label
        forecastConsumption.push(currentSum / 4)
        if (data.productionTimeSeries && data.productionTimeSeries.length === data.consumptionTimeSeries.length) {
          forecastProduction.push(currentProductionSum / 4)
        }
        forecastBalance.push((currentSum - currentProductionSum) / 4)
        currentSum = 0
        currentProductionSum = 0
      }
      if (item.endt > now && item.endt <= nowInThirtyDays) {
        currentSum += item.v
        if (data.productionTimeSeries && data.productionTimeSeries.length === data.consumptionTimeSeries.length) {
          currentProductionSum += data.productionTimeSeries[i].v
        }
      }
    }

    historyData.labels = labels
    Object.assign(historyData.datasets, {
      usageFromCommunity: fromCommunity,
      usageToCommunity: toCommunity,
      usageToSupplier: toSupplier,
      usageFromSupplier: fromSupplier,
      usageFromGrid: fromGrid,
      usageToGrid: toGrid,
      usageInverterProduction: inverterProduction,
      forecastConsumption,
      forecastBalance,
    })
    if (forecastProduction.length > 0) {
      historyData.datasets.forecastProduction = forecastProduction
    }
    historyData.rawDataSets.consumptionCLP = data.data
    historyData.received = true
  }

  function processHistoryData(data: HistoryData) {
    const cube = useCubeStore()
    const settings = useSettingsStore()
    const mapStore = useMapStore()

    const labels: Date[] = []
    const fromCommunity: number[] = []
    const fromSupplier: number[] = []
    const toCommunity: number[] = []
    const toSupplier: number[] = []
    const communityConsumable: number[] = []
    const inverterProduction: (number | null)[] = []
    const batterySOC: (number | null)[] = []
    const totalPrice: (number | null)[] = []
    const totalPriceProduction: (number | null)[] = []

    let outgoingPrices: Record<string, any> = {}
    let dayAvgRaw: { amount: number; energy: number }[] = []
    let dayAvgProductionRaw: { amount: number; energy: number }[] = []
    let stickyHasProduced = false

    const now = moment()

    const useCurrentConPrice = (data: HistoryData) =>
      settings.contractData.plans.con?.nextVatPrice && settings.contractData.plans.con?.nextPriceValidFrom
        ? moment(data.data[0].periodFrom).isBefore(moment(settings.contractData.plans.con.nextPriceValidFrom))
        : true

    const getFloatConPriceEUR = (data: HistoryData) =>
      useCurrentConPrice(data) ? settings.contractData.plans.con?.vatPrice : settings.contractData.plans.con?.nextVatPrice

    const useCurrentGenPrice = (data: HistoryData) =>
      settings.contractData.plans.gen?.nextVatPrice && settings.contractData.plans.gen?.nextPriceValidFrom
        ? moment(data.data[0].periodFrom).isBefore(moment(settings.contractData.plans.gen.nextPriceValidFrom))
        : true

    const getFloatGenPriceEUR = (data: HistoryData) =>
      useCurrentGenPrice(data) ? (settings.contractData.plans.gen?.standardPrice ?? 0) : settings.contractData.plans.gen?.nextPrice

    let i = 0
    for (const item of data.data) {
      labels.push(moment(item.periodFrom).toDate())
      fromCommunity.push(item.power.fromCommunity)
      fromSupplier.push(item.power.fromSupplier)
      toCommunity.push(-1 * item.power.toCommunity)
      toSupplier.push(-1 * item.power.toSupplier)
      communityConsumable.push(-1 * Math.min(item.power.communityConsumable ?? 0, 1000))

      if (moment(item.periodTo) <= now) {
        inverterProduction.push(item.power.inverterProduction)
        batterySOC.push(item.batterySOC)
      } else {
        inverterProduction.push(null)
        batterySOC.push(null)
      }

      let tempTotalCommunityEnergy = 0
      let tempTotalPrice = (data.consumptionPriceTimeSeries[i] ?? 0) * item.power.fromSupplier

      if (cube.supplier.name === 'eFriends Energy GmbH' && checkFloatPlan()) {
        tempTotalPrice = getFloatConPriceEUR(data) * 100 * item.power.fromSupplier
      } else if (cube.supplier.name === 'eFriends Energy GmbH' && checkFixPlan() && !checkFlexPlan()) {
        tempTotalPrice = (settings.contractData.plans.con?.vatPrice ?? 0) * 100 * item.power.fromSupplier
      }

      if (tradingStatistics.datasets.highRes?.producers[i]?.length > 0 && item.power.fromCommunity > 0) {
        tempTotalPrice += tradingStatistics.datasets.highRes.producers[i].reduce((acc: number, a: Record<string, number>) => {
          return acc + Object.keys(a).reduce((inneracc: number, cubeKey: string) => {
            tempTotalCommunityEnergy += a[cubeKey] * 4
            return inneracc + a[cubeKey] * 4 * (mapStore.globalProducerPrices[cubeKey]?.currentPrice ?? 0) * 100
          }, 0)
        }, 0)
      } else if (item.power.fromGrid === 0) {
        tempTotalPrice = null
      }

      totalPrice.push(
        tempTotalPrice === null || item.power.fromSupplier + tempTotalCommunityEnergy === 0
          ? cube.supplier.name === 'eFriends Energy GmbH' && !checkBusinessPlan() && !checkFlexPlan()
            ? (checkFloatPlan() ? getFloatConPriceEUR(data) : (settings.contractData.plans.con?.vatPrice ?? 0)) * 100
            : data.consumptionPriceTimeSeries[i]
          : tempTotalPrice / (item.power.fromSupplier + tempTotalCommunityEnergy)
      )
      dayAvgRaw.push({ amount: tempTotalPrice, energy: item.power.fromSupplier + tempTotalCommunityEnergy })

      let tempTotalCommunityProductionEnergy = 0
      let tempTotalPriceProduction = -(data.productionPriceTimeSeries[i] ?? 0) * item.power.toSupplier

      if (cube.supplier.name === 'eFriends Energy GmbH' && checkFloatPlan() && cube.hasProduction) {
        tempTotalPriceProduction = getFloatGenPriceEUR(data) * 100 * item.power.toSupplier
      } else if (cube.supplier.name === 'eFriends Energy GmbH' && checkFixPlan() && !checkFlexPlan() && cube.hasProduction) {
        tempTotalPriceProduction = (settings.contractData.plans.gen?.standardPrice ?? 0) * 100 * item.power.toSupplier
      }

      if (tradingStatistics.datasets.highRes?.consumers[i]?.length > 0 && item.power.toCommunity > 0) {
        tempTotalPriceProduction += tradingStatistics.datasets.highRes.consumers[i].reduce(
          (acc: number, a: Record<string, number>) => {
            return acc + Object.keys(a).reduce((inneracc: number, cubeKey: string) => {
              if (outgoingPrices[cubeKey] === undefined) {
                const prices = settings.consumerPrices[cubeKey]
                if (!prices) {
                  outgoingPrices[cubeKey] = settings.defaultSellingPrice
                } else {
                  outgoingPrices[cubeKey] = typeof prices.currentPrice !== 'undefined' ? prices.currentPrice : settings.defaultSellingPrice
                }
              }
              tempTotalCommunityProductionEnergy += a[cubeKey] * 4
              return inneracc + a[cubeKey] * 4 * outgoingPrices[cubeKey] * 100
            }, 0)
          },
          0
        )
      } else if (item.power.toGrid === 0) {
        tempTotalPriceProduction = null
      }

      const tempTotalPriceProd =
        tempTotalPriceProduction === null || item.power.toSupplier + tempTotalCommunityProductionEnergy === 0
          ? cube.supplier.name === 'eFriends Energy GmbH' && !checkBusinessPlan() && !checkFlexPlan() && cube.hasProduction
            ? (checkFloatPlan() ? getFloatGenPriceEUR(data) : (settings.contractData.plans.gen?.standardPrice ?? 0)) * 100
            : -(data.productionPriceTimeSeries[i] ?? 0)
          : tempTotalPriceProduction / (item.power.toSupplier + tempTotalCommunityProductionEnergy)

      totalPriceProduction.push(tempTotalPriceProd)
      dayAvgProductionRaw.push({ amount: tempTotalPriceProduction, energy: item.power.toSupplier + tempTotalCommunityProductionEnergy })
      stickyHasProduced ||= item.power.toGrid !== 0
      i++
    }

    const totalAvg = dayAvgRaw.reduce((acc, curr) => ({ amount: acc.amount + curr.amount, energy: acc.energy + curr.energy }), { amount: 0, energy: 0 })
    const totalAvgProduction = dayAvgProductionRaw.reduce((acc, curr) => ({ amount: acc.amount + curr.amount, energy: acc.energy + curr.energy }), { amount: 0, energy: 0 })

    const forecastConsumption: (number | null)[] = []
    const forecastProduction: (number | null)[] = []
    const forecastBalance: (number | null)[] = []
    const pastPrice: (number | null)[] = []
    const pastPriceProduction: (number | null)[] = []

    const timeSeries = DateTimeUtil.generateIsoRange(
      new Date(data.data[0].periodFrom),
      new Date(data.data[data.data.length - 1].periodFrom),
      15
    )

    for (let i = 0; i < timeSeries.length; i++) {
      const endt = timeSeries[i]
      const consumptionItem = data.consumptionTimeSeries[i]
      const productionItem = data.productionTimeSeries[i]

      if (moment(endt) <= now) {
        pastPrice.push(totalPrice[i] ?? 0)
        pastPriceProduction.push(totalPriceProduction[i] ?? 0)
      } else {
        pastPrice.push(null)
        pastPriceProduction.push(null)
      }

      if (moment(endt).add(-15, 'minutes') < now) {
        forecastConsumption.push(null)
        forecastProduction.push(null)
        forecastBalance.push(null)
        continue
      }

      forecastConsumption.push(consumptionItem?.v)
      if (data.productionTimeSeries.length === data.consumptionTimeSeries.length) {
        forecastProduction.push(productionItem?.v)
        forecastBalance.push((consumptionItem?.v ?? 0) - (productionItem?.v ?? 0))
      } else {
        forecastBalance.push(consumptionItem?.v)
      }
    }

    historyData.labels = labels
    Object.assign(historyData.datasets, {
      fromCommunity,
      toCommunity,
      toSupplier,
      fromSupplier,
      communityConsumable,
      inverterProduction,
      batterySOC,
      pastPrice,
      forecastConsumption,
      forecastBalance,
    })

    if (cube.hasProduction) {
      historyData.datasets.pastPriceProduction = pastPriceProduction
    }
    if (forecastProduction.length > 0) {
      historyData.datasets.forecastProduction = forecastProduction
    }

    if (cube.supplier.name === 'eFriends Energy GmbH' && checkFlexPlan()) {
      historyData.datasets.baseFrom = data.consumptionPriceTimeSeries
      if (cube.hasProduction) {
        historyData.datasets.baseTo = data.productionPriceTimeSeries
      }
    } else if (cube.supplier.name === 'eFriends Energy GmbH' && checkFloatPlan()) {
      historyData.datasets.baseFrom = new Array(labels.length).fill(getFloatConPriceEUR(data) * 100)
      if (cube.hasProduction) {
        historyData.datasets.baseTo = new Array(labels.length).fill(getFloatGenPriceEUR(data) * -100)
      }
    } else if (cube.supplier.name === 'eFriends Energy GmbH' && checkFixPlan()) {
      historyData.datasets.baseFrom = new Array(labels.length).fill((settings.contractData.plans.con?.vatPrice ?? 0) * 100)
      if (cube.hasProduction) {
        historyData.datasets.baseTo = new Array(labels.length).fill((settings.contractData.plans.gen?.standardPrice ?? 0) * -100)
      }
    }

    historyData.totals.avgPrice = totalAvg.energy === 0 ? false : totalAvg.amount / totalAvg.energy
    historyData.totals.avgPriceProduction =
      stickyHasProduced && totalAvgProduction.energy !== 0 ? totalAvgProduction.amount / totalAvgProduction.energy : false

    historyData.received = true
  }

  // --- Actions ---

  async function loadHistoryData(params: TradingStatisticsRequestParams) {
    const backend = useBackend()
    historyData.datasets = {}

    try {
      const [tradingStats, tradingStatsHighRes, reads, clpPriceTimeSeries, flexPriceTimeseries] = await Promise.all([
        backend.getTradingStatistics(params),
        backend.getTradingStatisticsHighRes(params),
        backend.getReads(params),
        backend.getClpValues(params),
        backend.getFlexPrices(params),
      ])

      const historyDataPayload: HistoryData = {
        data: reads,
        granularity: params.granularity,
        consumptionTimeSeries: clpPriceTimeSeries.consumptionTimeSeries,
        productionTimeSeries: clpPriceTimeSeries.productionTimeSeries,
        consumptionPriceTimeSeries: flexPriceTimeseries.consumptionPriceTimeSeries,
        productionPriceTimeSeries: flexPriceTimeseries.productionPriceTimeSeries,
      }

      const historyUsageData: HistoryUsageData = {
        data: reads,
        granularity: params.granularity,
        consumptionTimeSeries: clpPriceTimeSeries.consumptionTimeSeries,
        productionTimeSeries: clpPriceTimeSeries.productionTimeSeries,
      }

      // Order matters: tradingStats must be processed before historyData (highRes used in price calc)
      processTradingStatistics({ ...tradingStats, highRes: tradingStatsHighRes })

      if (params.granularity === '1day') {
        processHistoryUsageData(historyUsageData)
      } else {
        processHistoryData(historyDataPayload)
      }

      historyData.updatedAt = moment()
    } catch (error) {
      logError(`Error retrieving trading data ${error}.`)
    }
  }

  return {
    // State
    historySelectedDate,
    historyData,
    tradingStatistics,
    // Computed
    isDataReceived,
    tradingStatisticsReceived,
    chartData,
    tradingStatisticsLastUpdate,
    isFlexPlan,
    isFloatPlan,
    isFixPlan,
    isBusinessPlan,
    isMeterless,
    showPastFlex,
    showPastFloat,
    showPastFix,
    // Getters
    getTradingStatistics,
    tradingStatisticsAggregate,
    // Actions
    loadHistoryData,
  }
})
